'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import LeadCaptureModal from './LeadCaptureModal';

const navItems = [
  { label: 'Loans', href: '#loans' },
  { label: 'Credit Cards', href: '#credit-cards' },
  { label: 'Investment', href: '#investment' },
  { label: 'Calculators', href: '#calculators' },
  { label: 'Insurance', href: '#insurance' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState('');

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e: React.MouseEvent, label: string) => {
    e.preventDefault();
    setSelectedService(label);
    setModalOpen(true);
    setMobileOpen(false);
  };

  return (
    <>
      <LeadCaptureModal isOpen={modalOpen} onClose={() => setModalOpen(false)} service={selectedService} />
      <header className={`sticky top-0 z-50 transition-shadow duration-300 ${scrolled ? 'shadow-md' : ''}`}>
        {/* Top utility bar */}
        <div className="bg-orange-600 border-b border-orange-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-end gap-4 py-2">
              <button className="flex items-center gap-2 text-sm text-white hover:text-orange-100 transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <span className="font-medium">Talk to Expert: +91 9606009802</span>
              </button>
              <span className="text-orange-300">|</span>
              <button className="flex items-center gap-2 text-sm text-white hover:text-orange-100 transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                <span className="font-medium">Get The App</span>
              </button>
            </div>
          </div>
        </div>
        {/* Main nav */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-20">
              {/* Logo */}
              <Link href="/" className="flex items-center gap-3 flex-shrink-0">
                <div className="relative flex-shrink-0">
                  <img
                    src="/assets/images/Logo-1775403336481.jpg"
                    alt="KARFIN Logo"
                    className="h-20 w-auto object-contain"
                  />
                </div>
                <div>
                  <span className="text-xl font-bold text-gray-900 tracking-wide" style={{ fontFamily: 'Poppins, sans-serif' }}>KARFIN</span>
                  <div className="text-xs font-bold text-gray-700" style={{ letterSpacing: '0.02em' }}>Karfin Consulting Pvt Ltd.</div>
                </div>
              </Link>

              {/* Desktop Nav */}
              <nav className="hidden lg:flex items-center gap-1">
                {navItems?.map((item) => (
                  <div key={item?.label} className="relative group">
                    <a
                      href={item?.href}
                      onClick={(e) => handleNavClick(e, item.label)}
                      className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 hover:text-primary transition-colors rounded-md hover:bg-orange-50 cursor-pointer"
                    >
                      {item?.label}
                      <svg className="w-3 h-3 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </a>
                  </div>
                ))}
              </nav>

              {/* Sign In */}
              <div className="flex items-center gap-3">
                <button className="hidden sm:block px-4 py-1.5 text-sm font-medium text-primary border border-primary rounded hover:bg-primary hover:text-white transition-all duration-200">
                  Sign In
                </button>
                <button
                  className="lg:hidden p-2 text-gray-700"
                  onClick={() => setMobileOpen(!mobileOpen)}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {mobileOpen
                      ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    }
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Mobile menu */}
          {mobileOpen && (
            <div className="lg:hidden border-t border-gray-200 bg-white">
              <div className="px-4 py-3 space-y-1">
                {navItems?.map((item) => (
                  <a
                    key={item?.label}
                    href={item?.href}
                    className="block px-3 py-2 text-sm font-medium text-gray-700 hover:text-primary hover:bg-orange-50 rounded-md transition-colors cursor-pointer"
                    onClick={(e) => handleNavClick(e, item.label)}
                  >
                    {item?.label}
                  </a>
                ))}
                <button className="w-full mt-2 px-4 py-2 text-sm font-medium text-primary border border-primary rounded hover:bg-primary hover:text-white transition-all">
                  Sign In
                </button>
              </div>
            </div>
          )}
        </div>
      </header>
    </>
  );
}
